import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { FILTER_MENU, SCREEN_EDGE_GAP } from '@constants/layout';
import { FILTERS } from '@locales/id/messages';
import { useDismiss } from '@lib/hooks';
import { Icon } from './Icon';
import { ExitGhost } from './useExitGhost';

/** What arrow keys move between inside a popup: options, checkboxes, the search box. */
const ITEM = '[data-menu-item]:not(:disabled)';

interface Props {
  /** the button's text: the filter's name ("Kategori"), or its current value ("Kategori: Lokasi", "7 hari") */
  text: ReactNode;
  /** the filter is set: the button shows it */
  active?: boolean;
  /** the popup's accessible name */
  label: string;
  /** popup width (px; narrowed to the screen) */
  width?: number;
  /** the popup opened or closed (a caller that fetches its options does so only while open) */
  onOpenChange?: (open: boolean) => void;
  /** the popup's content; `close()` closes it and puts the focus back on the button */
  children: (close: () => void) => ReactNode;
  /** another look for the button (e.g. a map control) instead of the toolbar's; it then has no caret */
  triggerClass?: string;
  /** `end`: the popup lines up with the button's right edge (a button at the right of the screen) */
  align?: 'start' | 'end';
}

/**
 * A compact filter button with a popup under it (a list, a checklist, a calendar). Enter, Space or ↓ opens it and
 * focuses its first option (or `[data-autofocus]`); ↑/↓, Home/End move between `[data-menu-item]` elements; Esc
 * closes it and returns the focus to the button; a press outside, or Tab leaving it, closes it. The popup stays
 * on screen: it lines up with the button's right edge when it would leave the screen, and scrolls inside when the
 * page is short.
 */
export function FilterMenu({ text, active, label, width = FILTER_MENU.width, onOpenChange, children, triggerClass, align = 'start' }: Props) {
  const [open, setOpenState] = useState(false);
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const popupId = useId();
  const changed = useRef(onOpenChange);
  changed.current = onOpenChange;

  const setOpen = useCallback((o: boolean, refocus = false) => {
    setOpenState(o);
    changed.current?.(o);
    if (!o) {
      setPlace(null);
      if (refocus) trigger.current?.focus();
    }
  }, []);
  const close = useCallback(() => setOpen(false, true), [setOpen]);
  const dismiss = useCallback((escaped: boolean) => setOpen(false, escaped), [setOpen]);
  useDismiss(wrap, open, dismiss);

  // Fixed to the viewport, so a card that clips its overflow (a short list) can never cut it off: under the button,
  // above it when there is more room there, as tall as the room allows; kept on the button while the page scrolls.
  useLayoutEffect(() => {
    if (!open) return;
    let frame = 0;
    const measure = () => {
      if (!trigger.current) return;
      const t = trigger.current.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      const w = Math.min(width, vw - 2 * SCREEN_EDGE_GAP);
      const left = Math.min(Math.max(SCREEN_EDGE_GAP, align === 'end' ? t.right - w : t.left), vw - SCREEN_EDGE_GAP - w);
      const below = vh - t.bottom - FILTER_MENU.offset - SCREEN_EDGE_GAP;
      const above = t.top - FILTER_MENU.offset - SCREEN_EDGE_GAP;
      const natural = panel.current?.scrollHeight ?? 0;
      const up = natural > below && above > below;
      const maxHeight = Math.max(FILTER_MENU.minHeight, up ? above : below);
      // it opens from its button (the motion standard: menus scale and fade from their anchor)
      const origin = { '--pop-origin': `${up ? 'bottom' : 'top'} ${align === 'end' ? 'right' : 'left'}` } as CSSProperties;
      setPlace(up ? { ...origin, left, width: w, maxHeight, bottom: vh - t.top + FILTER_MENU.offset } : { ...origin, left, width: w, maxHeight, top: t.bottom + FILTER_MENU.offset });
    };
    const later = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('resize', later);
    window.addEventListener('scroll', later, { capture: true, passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', later);
      window.removeEventListener('scroll', later, { capture: true });
    };
  }, [open, width, align]);

  // the focus moves into the popup once it is placed
  const placed = place !== null;
  useEffect(() => {
    if (!placed) return;
    const box = panel.current;
    (box?.querySelector<HTMLElement>('[data-autofocus]') ?? box?.querySelector<HTMLElement>(`${ITEM}[tabindex="0"], ${ITEM}:not([tabindex])`))?.focus({ preventScroll: true });
  }, [placed]);

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <div
      className="fmenu"
      ref={wrap}
      onBlur={(e) => {
        // Tab (or a click on something focusable) left the button and its popup
        if (open && e.relatedTarget && !wrap.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button
        ref={trigger}
        type="button"
        className={triggerClass ?? `fmenu-trigger ${active ? 'is-active' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        onClick={() => setOpen(!open)}
        onKeyDown={onTriggerKey}
      >
        {triggerClass ? text : <span className="fmenu-text">{text}</span>}
        {!triggerClass && <Icon name="down" size={16} />}
      </button>
      {open && <ExitGhost of={panel} kind="pop" />}
      {open && (
        // tabIndex -1: a press on a checklist's text (a label) keeps the focus in the popup; without it the focus went
        // to the page's focusable <main>, which read as "left the menu" and closed it before the box was ticked
        <div id={popupId} ref={panel} role="dialog" aria-label={label} tabIndex={-1} className="fmenu-pop" style={place ?? { visibility: 'hidden' }} onKeyDown={moveMenuFocus}>
          {children(close)}
        </div>
      )}
    </div>
  );
}

/**
 * ↑/↓ (wrapping), Home/End between the `[data-menu-item]` elements inside the element handling the key: a popup,
 * or a section of the phone's filter sheet. Keys a child already handled (the calendar's arrows) are left alone.
 */
export function moveMenuFocus(e: KeyboardEvent<HTMLElement>) {
  if (e.defaultPrevented || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
  const typing = e.target instanceof HTMLInputElement && e.target.type !== 'checkbox' && e.target.type !== 'radio';
  if (typing && (e.key === 'Home' || e.key === 'End')) return;
  const items = [...e.currentTarget.querySelectorAll<HTMLElement>(ITEM)].filter((el) => el.offsetParent !== null);
  if (!items.length) return;
  e.preventDefault();
  const i = items.indexOf(document.activeElement as HTMLElement);
  const next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : e.key === 'ArrowDown' ? (i + 1) % items.length : i <= 0 ? items.length - 1 : i - 1;
  items[next].focus();
}

/**
 * One choice in a popup list (radio semantics: exactly one is checked in its group). Only the checked one, or
 * `focusable`, is in the Tab order; arrows reach the others.
 */
export function MenuChoice({ checked, onSelect, focusable, lead, sub, meta, children }: { checked: boolean; onSelect: () => void; focusable?: boolean; lead?: ReactNode; sub?: ReactNode; meta?: ReactNode; children: ReactNode }) {
  return (
    <button type="button" role="radio" aria-checked={checked} tabIndex={checked || focusable ? 0 : -1} className="fmenu-option" data-menu-item onClick={onSelect}>
      {lead}
      <span className="fmenu-option-text">
        <span className="fmenu-option-title">{children}</span>
        {sub && <span className="fmenu-option-sub">{sub}</span>}
      </span>
      {meta && <span className="fmenu-option-meta">{meta}</span>}
      <span className="fmenu-check" aria-hidden="true">{checked && <Icon name="check" size={16} />}</span>
    </button>
  );
}

/** One option of a checklist (several may be checked). */
export function MenuCheck({ checked, onToggle, children }: { checked: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <label className="fmenu-option fmenu-checkopt">
      <input type="checkbox" checked={checked} onChange={onToggle} data-menu-item />
      <span className="fmenu-option-title">{children}</span>
    </label>
  );
}

/** Phones: the one "Filter" button that opens the filters in a sheet, with how many are set. */
export function FilterSheetButton({ count, open, onClick }: { count: number; open: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`fmenu-trigger fmenu-sheet-btn ${count ? 'is-active' : ''}`} aria-haspopup="dialog" aria-expanded={open} onClick={onClick}>
      <Icon name="filter" size={18} />
      <span>{FILTERS.button}</span>
      {count > 0 && (
        <span className="fmenu-badge">
          <span aria-hidden="true">{count}</span>
          <span className="sr-only">, {FILTERS.activeCount(count)}</span>
        </span>
      )}
    </button>
  );
}
